"use client";

/**
 * LoginForm – the shared email/password sign-in form for the light login.
 *
 * Identical fields on desktop and mobile (size per viewport via `density`,
 * applied by CSS in login.css). Wires to the real auth: publicAuthClient.signIn.email,
 * translates errors to German, redirects to "/home" (or a safe `?next=`) on success, supports
 * Enter-to-submit, a loading state, and an inline error banner.
 */

import { safeLoginNextPath } from "@/lib/login-next-path";
import { isPortalEntryClient } from "@/lib/portal-sign-in-path";
import { publicAuthClient } from "@/lib/public-auth-client";
import { ld } from "@/lib/theme/light-dark";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { type FormEvent, type ReactElement, useState } from "react";
import {
  ArrowRightIcon,
  EyeIcon,
  EyeOffIcon,
  LockIcon,
  MailIcon,
  Spinner,
} from "./login-form-icons";
import { type LoginFormNotice, loginFormNotice } from "./login-form-notice";
import {
  BANNER_STYLE,
  ERROR_TONE_STYLE,
  ICON_WRAP_STYLE,
  INPUT_BASE_STYLE,
  LABEL_STYLE,
  TOGGLE_STYLE,
} from "./login-form-styles";
import {
  ACCENT,
  ACCENT_SHADOW,
  ACCENT_TEXT,
  authErrorKey,
  translateAuthError,
} from "./login-shared";

/** "responsive": compact below md via CSS, so SSR already shows the phone size. */
type LoginFormDensity = "regular" | "compact" | "responsive";

type LoginFormProps = {
  /** Field sizing; default "regular" (wide card). */
  readonly density?: LoginFormDensity;
  /** Accent color for the submit button + links (org brand color on portal). */
  readonly accent?: string;
  /** Shadow tint under the submit button, matched to `accent`. */
  readonly accentShadow?: string;
  /** Text color on the accent-filled submit button (for light brand colors). */
  readonly accentText?: string;
  /** Customer portal: tenant wording (placeholder, „Profil“, ended access). */
  readonly portal?: boolean;
};

export function LoginForm({
  density = "regular",
  accent = ACCENT,
  accentShadow = ACCENT_SHADOW,
  accentText = ACCENT_TEXT,
  portal = false,
}: LoginFormProps): ReactElement {
  const t = useTranslations("public.loginForm");
  const tAuthError = useTranslations("public.loginShared");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Read once on mount: LoginScreen strips both params right afterwards, so a
  // live read would go blank before anyone could see it.
  const searchParams = useSearchParams();
  const [emailChangeResult] = useState<LoginFormNotice | null>(() =>
    loginFormNotice(t, searchParams, portal),
  );

  const canSubmit = email.length > 0 && password.length > 0 && !isLoading;

  async function handleSignIn(): Promise<void> {
    setErrorMessage(null);
    setIsLoading(true);
    const result = await publicAuthClient.signIn.email({ email, password });

    if (result.error) {
      setIsLoading(false);
      const code =
        typeof result.error.code === "string" ? result.error.code : "";
      const msg = result.error.message ?? "";
      const translated: string =
        translateAuthError(tAuthError, code.length > 0 ? code : msg) ||
        translateAuthError(tAuthError, msg);
      // A revoked tenant has no account left: name that case instead of only „wrong password“.
      const wrongCredentials: boolean = [code, msg].some(
        (value) => authErrorKey(value) === "ungueltigeEMailPasswort",
      );
      setErrorMessage(
        portal && wrongCredentials ? t("ungueltigOderZugangBeendet") : translated,
      );
      return;
    }

    // Staff hosts go straight to the app entry (`/home`; `/` is marketing),
    // or back to the OAuth consent page that sent them here.
    const onPortalHost = isPortalEntryClient();
    window.location.replace(
      onPortalHost
        ? "/portal-launch"
        : (safeLoginNextPath(searchParams.get("next")) ?? "/home"),
    );
  }

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;
    await handleSignIn();
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mklogin-form"
      data-density={density}
      style={{
        marginTop: "var(--mklogin-form-top)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--mklogin-form-gap)",
      }}
    >
      {/* E-Mail */}
      <div>
        <label
          htmlFor="mklogin-email"
          style={{
            ...LABEL_STYLE,
            display: "block",
            marginBottom: "var(--mklogin-label-gap)",
          }}
        >
          {t("eMailAdresse")}
        </label>
        <div style={{ position: "relative" }}>
          <span style={ICON_WRAP_STYLE}>
            <MailIcon />
          </span>
          <input
            id="mklogin-email"
            className="mklogin-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={portal ? t("nameBeispielDe") : t("nameVerwaltungDe")}
            autoComplete="username"
            disabled={isLoading}
            style={{
              ...INPUT_BASE_STYLE,
              padding: "0 14px 0 var(--mklogin-input-pl)",
            }}
          />
        </div>
      </div>

      {/* Passwort */}
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            marginBottom: "var(--mklogin-label-gap)",
          }}
        >
          <label htmlFor="mklogin-password" style={LABEL_STYLE}>
            {t("passwort")}
          </label>
          <Link
            href="/forgot-password"
            className="mklogin-link mklogin-tap"
            style={{
              fontSize: 12,
              color: accent,
              textDecoration: "none",
              fontWeight: 500,
            }}
          >
            {t("passwortVergessen")}
          </Link>
        </div>
        <div style={{ position: "relative" }}>
          <span style={ICON_WRAP_STYLE}>
            <LockIcon />
          </span>
          <input
            id="mklogin-password"
            className="mklogin-input"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("passwort2")}
            autoComplete="current-password"
            disabled={isLoading}
            style={{
              ...INPUT_BASE_STYLE,
              padding:
                "0 var(--mklogin-input-pr) 0 var(--mklogin-input-pl)",
            }}
          />
          <button
            type="button"
            className="mklogin-pw-toggle"
            onClick={() => setShowPassword((s) => !s)}
            aria-label={
              showPassword ? t("passwortVerbergen") : t("passwortAnzeigen")
            }
            style={TOGGLE_STYLE}
          >
            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        </div>
      </div>

      {emailChangeResult && errorMessage === null ? (
        <div
          role="status"
          style={{
            ...BANNER_STYLE,
            ...(emailChangeResult.tone === "error"
              ? ERROR_TONE_STYLE
              : {
                  border: `1px solid ${ld("rgba(11,15,25,0.14)", "rgba(250,250,250,0.14)")}`,
                  background: ld("rgba(11,15,25,0.04)", "rgba(250,250,250,0.05)"),
                  color: ld("rgba(11,15,25,0.72)", "rgba(250,250,250,0.75)"),
                }),
            lineHeight: 1.5,
          }}
        >
          {emailChangeResult.text}
        </div>
      ) : null}

      {errorMessage ? (
        <div role="alert" style={{ ...BANNER_STYLE, ...ERROR_TONE_STYLE }}>
          {errorMessage}
        </div>
      ) : null}

      <button
        type="submit"
        className="mklogin-submit"
        disabled={!canSubmit}
        style={{
          marginTop: 6,
          height: "var(--mklogin-button-h)",
          borderRadius: 12,
          border: "none",
          background: accent,
          color: accentText,
          fontFamily: "inherit",
          fontSize: 15.5,
          fontWeight: 600,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 9,
          boxShadow: `0 12px 26px -12px ${accentShadow}`,
        }}
      >
        {isLoading ? (
          <>
            <Spinner />
            {t("anmelden")}
          </>
        ) : (
          <>
            {t("anmelden")}
            <ArrowRightIcon />
          </>
        )}
      </button>
    </form>
  );
}
